import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  LinearProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import GppGoodIcon from "@mui/icons-material/GppGood";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import CloseIcon from "@mui/icons-material/Close";
import Chart from "react-apexcharts";
import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate } from "react-router";
import ReportBillModal from "./ReportBillModal";

const fmt = (n) =>
  `₹${(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtCompact = (n) =>
  `₹${(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

// "2026-03-14T..." or "2026-03-14"  ->  "14-03-2026"
const fmtDate = (d) => {
  if (!d) return "—";
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split("-");
  if (!y || !m || !day) return s;
  return `${day}-${m}-${y}`;
};
// bill is backdated when its billing month/year differs from the month it was counted in
const isBackdated = (bill) => {
  const b = String(bill.billDate || "").slice(0, 7); // "2025-10"
  const d = String(bill.date || "").slice(0, 7);     // "2026-01"
  return !!b && !!d && b !== d;
};
const monthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// A single small metric tile used across the header row.
const StatTile = ({ label, value, color, icon, hint, muted }) => {
  const theme = useTheme();
  const content = (
    <Box
      sx={{
        flex: 1,
        minWidth: 132,
        p: 1.5,
        borderRadius: 2.5,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: muted ? "transparent" : alpha(color || theme.palette.text.primary, 0.06),
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <Stack direction="row" spacing={0.6} alignItems="center">
        {icon}
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: 0.2 }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 800, color: color || "text.primary", lineHeight: 1.15 }}
      >
        {value}
      </Typography>
    </Box>
  );
  return hint ? (
    <Tooltip arrow title={hint}>
      <Box sx={{ display: "flex", flex: 1, minWidth: 132, cursor: "help" }}>{content}</Box>
    </Tooltip>
  ) : (
    content
  );
};

// Badge for a single billComponents[] row's "type" field (service / product)
const ComponentTypeChip = ({ type }) => {
  const theme = useTheme();
  const isService = type === "service";
  const isProduct = type === "product";
  return (
    <Chip
      size="small"
      icon={
        isService ? (
          <ContentCutIcon sx={{ fontSize: "0.85rem !important" }} />
        ) : isProduct ? (
          <Inventory2Icon sx={{ fontSize: "0.85rem !important" }} />
        ) : undefined
      }
      label={type ? type[0].toUpperCase() + type.slice(1) : "—"}
      sx={{
        fontWeight: 600,
        bgcolor: isService
          ? theme.palette.success.light
          : isProduct
          ? theme.palette.warning.light
          : theme.palette.grey[200],
        color: isService
          ? theme.palette.success.dark
          : isProduct
          ? theme.palette.warning.dark
          : theme.palette.text.secondary,
      }}
    />
  );
};

const StaffPerformanceDetails = ({ staffData, selectedMonth: initialMonth, ownerId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(initialMonth || "all");
  const [billFilter, setBillFilter] = useState("all"); // all | service | product | other
  const [componentsDialogBill, setComponentsDialogBill] = useState(null); // the bill whose components are being viewed
  const [billModal, setBillModal] = useState({ open: false, billId: null, billType: null });

  const monthlyList = staffData?.monthly || [];
  const incentiveBills = staffData?.incentiveBills || [];

  // Filter incentive bills for the selected month (or all year) and by type.
  const filteredBills = useMemo(() => {
    let bills = incentiveBills;
    if (selectedMonth !== "all") {
      bills = bills.filter((b) => {
        const src = b.date || b.billDate;
        if (!src) return false;
        const m = parseInt(String(src).slice(5, 7), 10);
        return m === selectedMonth;
      });
    }
  if (billFilter === "service") {
      bills = bills.filter((b) => b.isServiceIncludeInIncentive);
    } else if (billFilter === "product") {
      bills = bills.filter((b) => b.isProductIncludeInIncentive);
    } else if (billFilter === "other") {
      bills = bills.filter(
        (b) => !b.isServiceIncludeInIncentive && !b.isProductIncludeInIncentive
      );
    } else if (billFilter === "backdated") {
      bills = bills.filter(isBackdated);
    }
    return bills;
  }, [incentiveBills, selectedMonth, billFilter]);

  // Totals shown in the sticky table footer
  const billTotals = useMemo(() => {
    const t = { service: 0, product: 0, other: 0, all: 0, count: filteredBills.length };
    filteredBills.forEach((b) => {
      const amt = Number(b.amount || 0);
      t.all += amt;
      if (b.isServiceIncludeInIncentive) t.service += amt;
      else if (b.isProductIncludeInIncentive) t.product += amt;
      else t.other += amt;
    });
    return t;
  }, [filteredBills]);

  // Bill-type breakdown for the incentive pie chart
  const incentiveTypeBreakdown = useMemo(() => {
    let bills = incentiveBills;
    if (selectedMonth !== "all") {
      bills = bills.filter((b) => {
        const src = b.billDate || b.date;
        if (!src) return false;
        const m = parseInt(String(src).slice(5, 7), 10);
        return m === selectedMonth;
      });
    }
    const t = { service: 0, product: 0, other: 0 };
    bills.forEach((b) => {
      const amt = Number(b.amount || 0);
      if (b.isServiceIncludeInIncentive) t.service += amt;
      else if (b.isProductIncludeInIncentive) t.product += amt;
      else t.other += amt;
    });
    return t;
  }, [incentiveBills, selectedMonth]);

    // Totals for whatever bill's components are currently open in the dialog
  const componentsTotals = useMemo(() => {
    const comps = componentsDialogBill?.billComponents || [];
    return comps.reduce(
      (acc, c) => {
        acc.amount += Number(c.amount || 0);
        acc.incentive += Number(c.incentiveAmount || 0);
        if (c.type === "service") acc.serviceAmount += Number(c.amount || 0);
        else if (c.type === "product") acc.productAmount += Number(c.amount || 0);
        return acc;
      },
      { amount: 0, incentive: 0, serviceAmount: 0, productAmount: 0 }
    );
  }, [componentsDialogBill]);

  if (!staffData) {
    return (
      <Card>
        <CardContent>
          <Typography>Select a staff member to see their performance.</Typography>
        </CardContent>
      </Card>
    );
  }

  const handleMonthChange = (event) => setSelectedMonth(event.target.value);

 const openBill = (billId, billType) => {
  if (!billId) return;
  setBillModal({ open: true, billId, billType });
};

  let pieChartSeries = [0, 0];
  let chartTitle = `Contribution — ${staffData.staffName}`;
  let detailsData = {
    service: 0,
    product: 0,
    incentive: 0,
    totalWork: 0,
    backdatedService: 0,
    backdatedProduct: 0,
    combinedTotal: 0,
    dueService: 0,
    dueProduct: 0,
  };

  if (selectedMonth === "all") {
    const totalService = monthlyList.reduce((sum, d) => sum + (d.totalServiceAmount || 0), 0);
    const totalProduct = monthlyList.reduce((sum, d) => sum + (d.totalProductAmount || 0), 0);
    const totalIncentive = monthlyList.reduce((sum, d) => sum + (d.monthlyIncentiveAmount || 0), 0);
    const totalDueService = monthlyList.reduce((sum, d) => sum + (d.duePaymentServiceAmount || 0), 0);
    const totalDueProduct = monthlyList.reduce((sum, d) => sum + (d.duePaymentProductAmount || 0), 0);
    pieChartSeries = [totalService, totalProduct];
    chartTitle = `Yearly Contribution — ${staffData.staffName}`;
    detailsData = {
      service: totalService,
      product: totalProduct,
      incentive: totalIncentive,
      totalWork: staffData.totalYearWork || 0,
      backdatedService: staffData.totalYearCreatedMismatchService || 0,
      backdatedProduct: staffData.totalYearCreatedMismatchProduct || 0,
      combinedTotal:
        totalService +
        totalProduct +
        (staffData.totalYearCreatedMismatchService || 0) +
        (staffData.totalYearCreatedMismatchProduct || 0),
      dueService: staffData.totalYearDuePaymentService ?? totalDueService,
      dueProduct: staffData.totalYearDuePaymentProduct ?? totalDueProduct,
    };
  } else {
    const monthData = monthlyList.find((m) => m.month === selectedMonth);
    if (monthData) {
      pieChartSeries = [monthData.totalServiceAmount || 0, monthData.totalProductAmount || 0];
      chartTitle = `${monthNames[selectedMonth - 1]} Contribution — ${staffData.staffName}`;
      detailsData = {
        service: monthData.totalServiceAmount || 0,
        product: monthData.totalProductAmount || 0,
        incentive: monthData.monthlyIncentiveAmount || 0,
        totalWork: monthData.totalWorkAmount || 0,
        backdatedService: monthData.createdThisMonthBilledPrevMonthServiceAmount || 0,
        backdatedProduct: monthData.createdThisMonthBilledPrevMonthProductAmount || 0,
        combinedTotal: monthData.combinedServiceDueAmount ?? monthData.totalWorkAmount ?? 0,
        dueService: monthData.duePaymentServiceAmount || 0,
        dueProduct: monthData.duePaymentProductAmount || 0,
      };
    }
  }

  const backdatedTotal = detailsData.backdatedService + detailsData.backdatedProduct;
  const dueTotal = detailsData.dueService + detailsData.dueProduct;
  const target = Number(staffData.target || 0);
  const targetBasis =
    selectedMonth === "all" ? staffData.totalYearWork || 0 : detailsData.totalWork;
  const targetProgress = target > 0 ? Math.min((targetBasis / target) * 100, 100) : 0;
  const targetOver = target > 0 && targetBasis >= target;

  const pieChartOptions = {
    chart: { type: "donut" },
    labels: ["Service Amount", "Product Amount"],
    colors: [theme.palette.success.main, theme.palette.warning.main],
    legend: { position: "bottom", labels: { colors: theme.palette.text.secondary } },
    dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(1)}%` },
    stroke: { width: 2, colors: [theme.palette.background.paper] },
    plotOptions: {
      pie: {
        donut: {
          size: "72%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Sales This Period",
              formatter: () => fmt(detailsData.service + detailsData.product),
            },
          },
        },
      },
    },
  };

  const incentiveValuesAll = [
    incentiveTypeBreakdown.service,
    incentiveTypeBreakdown.product,
    incentiveTypeBreakdown.other,
  ];
  const incentiveLabelsAll = ["Service Incentive", "Product Incentive", "Other Incentive"];
  const incentiveColorsAll = [theme.palette.success.main, theme.palette.warning.main, theme.palette.grey[500]];
  const incentivePieHasData = incentiveValuesAll.some((v) => v > 0);

  const incentivePieSeries = incentivePieHasData
    ? incentiveValuesAll.filter((v) => v > 0)
    : [1];
  const incentivePieLabels = incentivePieHasData
    ? incentiveLabelsAll.filter((_, i) => incentiveValuesAll[i] > 0)
    : ["No incentive data"];
  const incentivePieColors = incentivePieHasData
    ? incentiveColorsAll.filter((_, i) => incentiveValuesAll[i] > 0)
    : [theme.palette.grey[300]];

  const incentivePieOptions = {
    chart: { type: "donut" },
    labels: incentivePieLabels,
    colors: incentivePieColors,
    legend: { position: "bottom", labels: { colors: theme.palette.text.secondary } },
    dataLabels: {
      enabled: incentivePieHasData,
      formatter: (val) => `${val.toFixed(1)}%`,
    },
    stroke: { width: 2, colors: [theme.palette.background.paper] },
    tooltip: { enabled: incentivePieHasData },
    plotOptions: {
      pie: {
        donut: {
          size: "72%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total Incentive",
              formatter: () =>
                fmt(incentiveTypeBreakdown.service + incentiveTypeBreakdown.product + incentiveTypeBreakdown.other),
            },
          },
        },
      },
    },
  };



  return (
    <Card elevation={0}>
      <CardContent sx={{ p: 0 }}>
        {/* HEADER + MONTH FILTER */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="h6">{chartTitle}</Typography>
          <FormControl sx={{ minWidth: 130 }} size="small">
            <InputLabel id="month-select-label">Month</InputLabel>
            <Select
              labelId="month-select-label"
              value={selectedMonth}
              label="Month"
              onChange={handleMonthChange}
            >
              <MenuItem value="all">All Year</MenuItem>
              {monthNames.map((name, index) => (
                <MenuItem key={index + 1} value={index + 1}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* INCENTIVE-TYPE BADGES */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Tooltip arrow title="Based on whether any of this staff's incentive bills this year were service-type">
            <Chip
              size="small"
              icon={<ContentCutIcon sx={{ fontSize: "0.9rem !important" }} />}
              label={staffData.isServiceIncludeInIncentive ? "Service Incentive: Active" : "Service Incentive: Inactive"}
              sx={{
                bgcolor: staffData.isServiceIncludeInIncentive ? theme.palette.success.light : theme.palette.grey[200],
                color: staffData.isServiceIncludeInIncentive ? theme.palette.success.dark : theme.palette.text.secondary,
                fontWeight: 600,
              }}
            />
          </Tooltip>
          <Tooltip arrow title="Based on whether any of this staff's incentive bills this year were product-type">
            <Chip
              size="small"
              icon={<Inventory2Icon sx={{ fontSize: "0.9rem !important" }} />}
              label={staffData.isProductIncludeInIncentive ? "Product Incentive: Active" : "Product Incentive: Inactive"}
              sx={{
                bgcolor: staffData.isProductIncludeInIncentive ? theme.palette.warning.light : theme.palette.grey[200],
                color: staffData.isProductIncludeInIncentive ? theme.palette.warning.dark : theme.palette.text.secondary,
                fontWeight: 600,
              }}
            />
          </Tooltip>
        </Stack>

        {/* KEY STAT TILES */}
        <Stack direction="row" spacing={1.25} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <StatTile
            label="Service"
            value={fmtCompact(detailsData.service)}
            color={theme.palette.success.dark}
            icon={<ContentCutIcon sx={{ fontSize: "1rem", color: theme.palette.success.main }} />}
          />
          <StatTile
            label="Product"
            value={fmtCompact(detailsData.product)}
            color={theme.palette.warning.dark}
            icon={<Inventory2Icon sx={{ fontSize: "1rem", color: theme.palette.warning.main }} />}
          />
          <StatTile
            label="Incentive Earned"
            value={fmtCompact(detailsData.incentive)}
            color={theme.palette.primary.main}
            icon={<TrendingUpRoundedIcon sx={{ fontSize: "1rem", color: theme.palette.primary.main }} />}
            hint="Incentive is a separate payout, not counted as a sale."
          />
          {backdatedTotal > 0 && (
            <StatTile
              label="Backdated Bills"
              value={fmtCompact(backdatedTotal)}
              color={theme.palette.secondary.main}
              icon={<ReceiptLongIcon sx={{ fontSize: "1rem", color: theme.palette.secondary.main }} />}
              hint="Bills entered into the system in this period, but the billing date on them belongs to an earlier month/year."
            />
          )}
          {dueTotal > 0 && (
            <StatTile
              label="Payment Due"
              value={fmtCompact(dueTotal)}
              color={theme.palette.error.main}
              icon={<WarningAmberRoundedIcon sx={{ fontSize: "1rem", color: theme.palette.error.main }} />}
              hint={`Still uncollected on billed work: ${fmt(detailsData.dueService)} service + ${fmt(detailsData.dueProduct)} product`}
            />
          )}
        </Stack>

        <Grid container spacing={3} alignItems="stretch">
          {/* LEFT: SALES CHART + SUMMARY */}
          <Grid item xs={12} lg={5}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2, minHeight: 32 }}>
              <Typography variant="subtitle1" fontWeight="700">Sales Split</Typography>
            </Box>
            <Chart options={pieChartOptions} series={pieChartSeries} type="donut" height={260} />

            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2">Service Amount:</Typography>
                <Typography variant="body2" fontWeight="600">{fmt(detailsData.service)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2">Product Amount:</Typography>
                <Typography variant="body2" fontWeight="600">{fmt(detailsData.product)}</Typography>
              </Box>

              {backdatedTotal > 0 && (
                <Tooltip arrow title="Bills that were entered into the system in this period, but the billing date on them belongs to an earlier month/year.">
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, cursor: "help" }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <ReceiptLongIcon sx={{ fontSize: "1rem", color: theme.palette.secondary.main }} />
                      <Typography variant="body2" color="text.secondary">Backdated Bills:</Typography>
                      <InfoOutlinedIcon sx={{ fontSize: "0.85rem", color: "text.disabled" }} />
                    </Stack>
                    <Typography variant="body2" fontWeight="600" color="secondary.main">
                      {fmt(backdatedTotal)}
                    </Typography>
                  </Box>
                </Tooltip>
              )}

              {dueTotal > 0 && (
                <Tooltip
                  arrow
                  title={`Not yet collected from clients on billed work: ${fmt(detailsData.dueService)} service, ${fmt(detailsData.dueProduct)} product.`}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, cursor: "help" }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <WarningAmberRoundedIcon sx={{ fontSize: "1rem", color: theme.palette.error.main }} />
                      <Typography variant="body2" color="text.secondary">Payment Due:</Typography>
                      <InfoOutlinedIcon sx={{ fontSize: "0.85rem", color: "text.disabled" }} />
                    </Stack>
                    <Typography variant="body2" fontWeight="600" color="error.main">
                      {fmt(dueTotal)}
                    </Typography>
                  </Box>
                </Tooltip>
              )}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 1.5,
                  pt: 1.5,
                  borderTop: "1px solid " + theme.palette.divider,
                }}
              >
                <Tooltip arrow title="Combined Total = Service + Product (this period) + Backdated Bills">
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ cursor: "help" }}>
                    <Typography variant="subtitle1" fontWeight="700">Combined Total:</Typography>
                    <InfoOutlinedIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
                  </Stack>
                </Tooltip>
                <Typography variant="subtitle1" fontWeight="700" color="primary.main">
                  {fmt(detailsData.combinedTotal)}
                </Typography>
              </Box>

              <Tooltip arrow title="Incentive is a separate payout, not counted as a sale.">
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, cursor: "help" }}>
                  <Typography variant="body2" color="text.secondary">Incentive Earned:</Typography>
                  <Typography variant="body2" fontWeight="600" color="error.main">
                    {fmt(detailsData.incentive)}
                  </Typography>
                </Box>
              </Tooltip>

              <Divider sx={{ my: 1.5 }} />

              {/* TARGET PROGRESS */}
              <Box>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                  <GppGoodIcon sx={{ fontSize: "1rem", color: theme.palette.info.main }} />
                  <Typography variant="body2" fontWeight="700" sx={{ flexGrow: 1 }}>
                    Target Progress
                  </Typography>
                  <Chip
                    size="small"
                    label={target > 0 ? fmt(target) : "Not set"}
                    sx={{
                      fontWeight: 800,
                      bgcolor: target > 0 ? theme.palette.info.light : theme.palette.grey[200],
                      color: target > 0 ? theme.palette.info.dark : theme.palette.text.secondary,
                    }}
                  />
                </Stack>
                {target > 0 ? (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={targetProgress}
                      sx={{
                        height: 8,
                        borderRadius: 5,
                        bgcolor: theme.palette.grey[200],
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 5,
                          bgcolor: targetOver ? theme.palette.success.main : theme.palette.info.main,
                        },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {fmt(targetBasis)} of {fmt(target)} ({targetProgress.toFixed(0)}%)
                      {targetOver ? " — target met" : ""}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No target has been set for this staff member yet.
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* RIGHT: INCENTIVE BILL BREAKDOWN */}
          <Grid item xs={12} lg={7}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2, minHeight: 32 }}>
              <Tooltip arrow title="Every bill that contributed to this staff member's incentive. Use Open to view the bill, or Components to see its line items.">
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ cursor: "help" }}>
                  <Typography variant="subtitle1" fontWeight="700">Incentive Bill Breakdown</Typography>
                  <InfoOutlinedIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
                </Stack>
              </Tooltip>
            </Box>

            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 1,
              }}
            >
              <Chart options={incentivePieOptions} series={incentivePieSeries} type="donut" height={260} />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5, mb: 2 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={billFilter}
                onChange={(e, val) => val && setBillFilter(val)}
              >
                <ToggleButton value="all">All Bill</ToggleButton>
                <ToggleButton value="service">Service Bill</ToggleButton>
                <ToggleButton value="product">Product Bill</ToggleButton>
                <ToggleButton value="other">Other Bill</ToggleButton>
                <ToggleButton value="backdated">Backdated Bill</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <TableContainer
              sx={{
                maxHeight: 420,
                minHeight: 240,
                overflowY: "auto",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap", textAlign: "center" }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: "center" }}>Client Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: "center" }}>Type</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap", textAlign: "center" }}>
                      Bill Total Amount
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap", textAlign: "center" }}>
                      Incentive
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, whiteSpace: "nowrap", textAlign: "center" }}>
                      Components
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, whiteSpace: "nowrap", textAlign: "center" }}>
                      Bill
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredBills.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                        <Typography variant="body2" color="text.secondary">
                          No incentive bills in this period. Try another month or filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {filteredBills.map((bill, idx) => {
                    const compCount = bill.billComponents?.length || 0;
                    return (
                      <TableRow key={`${bill.billId || "na"}-${idx}`} hover>
                        <TableCell sx={{ whiteSpace: "nowrap", textAlign: "center" }}>
                          {fmtDate(bill.billDate || bill.date)}
                        </TableCell>

                        <TableCell sx={{ textAlign: "center" }}>
                          <Typography variant="body2" fontWeight="600">
                            {bill.clientName || "—"}
                          </Typography>
                        </TableCell>
                          

                        <TableCell sx={{ textAlign: "center" }}>
                            <Chip
                              size="small"
                              label={
                                bill.isServiceIncludeInIncentive && bill.isProductIncludeInIncentive
                                  ? "Service + Product"
                                  : bill.isServiceIncludeInIncentive
                                  ? "Service"
                                  : bill.isProductIncludeInIncentive
                                  ? "Product"
                                  : "Other"
                              }
                              sx={{
                                fontWeight: 600,
                                textTransform: "capitalize",
                                bgcolor: bill.isServiceIncludeInIncentive
                                  ? theme.palette.success.light
                                  : bill.isProductIncludeInIncentive
                                  ? theme.palette.warning.light
                                  : theme.palette.grey[200],
                                color: bill.isServiceIncludeInIncentive
                                  ? theme.palette.success.dark
                                  : bill.isProductIncludeInIncentive
                                  ? theme.palette.warning.dark
                                  : theme.palette.text.secondary,
                              }}
                            />
                          </TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>

                          <Typography variant="body2" fontWeight="600">
                            {bill.billTotalAmount || "—"}
                          </Typography>
                        </TableCell>                  
                        <TableCell align="right" sx={{ textAlign: "center" }}>
                          <Typography variant="body2" fontWeight="600">{fmt(bill.amount)}</Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip arrow title={compCount ? `${compCount} line item${compCount > 1 ? "s" : ""} on this bill` : "No line items recorded"}>
                            <span>
                              <Button
                                size="small"
                                variant="text"
                                disabled={!compCount}
                                startIcon={<ListAltRoundedIcon sx={{ fontSize: "0.9rem !important" }} />}
                                onClick={() => setComponentsDialogBill(bill)}
                                sx={{ fontWeight: 600, textTransform: "none", whiteSpace: "nowrap" }}
                              >
                                View ({compCount})
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip arrow title={bill.billId ? "Open this bill" : "Bill record not found"}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!bill.billId}
                                endIcon={<OpenInNewIcon sx={{ fontSize: "0.9rem !important" }} />}
                              onClick={() => openBill(bill.billId, bill.billType)}
                                sx={{ fontWeight: 600, textTransform: "none", whiteSpace: "nowrap" }}
                              >
                                Open
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>

                {filteredBills.length > 0 && (
                  <TableFooter
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      bgcolor: theme.palette.background.paper,
                      boxShadow: `0 -1px 0 ${theme.palette.divider}`,
                    }}
                  >
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontWeight: 700, color: "text.primary" }}>
                        Service Incentive Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: theme.palette.success.dark }}>
                        {fmt(billTotals.service)}
                      </TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontWeight: 700, color: "text.primary" }}>
                        Product Incentive Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: theme.palette.warning.dark }}>
                        {fmt(billTotals.product)}
                      </TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                    {billTotals.other > 0 && (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ fontWeight: 700, color: "text.primary" }}>
                          Other Incentive Total
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {fmt(billTotals.other)}
                        </TableCell>
                        <TableCell colSpan={2} />
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontWeight: 800, fontSize: "0.95rem", color: "text.primary" }}>
                        Grand Total ({billTotals.count} bills)
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 800, fontSize: "0.95rem", color: theme.palette.primary.main }}
                      >
                        {fmt(billTotals.all)}
                      </TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Each row is one incentive entry recorded against a bill. Use Components to see line items, or Open to view the full bill.
            </Typography>
          </Grid>
        </Grid>

        {/* BILL COMPONENTS DIALOG */}
        <Dialog
          open={Boolean(componentsDialogBill)}
          onClose={() => setComponentsDialogBill(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="800">
                {componentsDialogBill?.clientName || "Bill"} — Line Items
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {fmtDate(componentsDialogBill?.billDate || componentsDialogBill?.date)}
              </Typography>
            </Box>
            <IconButton onClick={() => setComponentsDialogBill(null)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            {(componentsDialogBill?.billComponents?.length || 0) === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No line items recorded on this bill.
                </Typography>
              </Box>
            ) : (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Incentive</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {componentsDialogBill.billComponents.map((c, i) => (
                      <TableRow key={i} hover>
                        <TableCell>
                          <ComponentTypeChip type={c.type} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">{c.name || "—"}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{fmt(c.amount)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={c.incentiveAmount ? 700 : 400}
                            color={c.incentiveAmount ? "success.main" : "text.secondary"}
                          >
                            {c.incentiveAmount ? fmt(c.incentiveAmount) : "—"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={2} sx={{ fontWeight: 800 }}>
                        Total ({componentsDialogBill.billComponents.length} items)
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>
                        {fmt(componentsTotals.amount)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: theme.palette.success.dark }}>
                        {fmt(componentsTotals.incentive)}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
                <Stack direction="row" spacing={1} sx={{ p: 1.5, flexWrap: "wrap" }} useFlexGap>
                  <Chip
                    size="small"
                    icon={<ContentCutIcon sx={{ fontSize: "0.85rem !important" }} />}
                    label={`Service: ${fmt(componentsTotals.serviceAmount)}`}
                    sx={{ bgcolor: theme.palette.success.light, color: theme.palette.success.dark, fontWeight: 600 }}
                  />
                  <Chip
                    size="small"
                    icon={<Inventory2Icon sx={{ fontSize: "0.85rem !important" }} />}
                    label={`Product: ${fmt(componentsTotals.productAmount)}`}
                    sx={{ bgcolor: theme.palette.warning.light, color: theme.palette.warning.dark, fontWeight: 600 }}
                  />
                </Stack>
              </>
            )}
          </DialogContent>
        </Dialog>
        <ReportBillModal
          open={billModal.open}
          billId={billModal.billId}
          ownerId={ownerId}
          billType={billModal.billType}
          onClose={() => setBillModal({ open: false, billId: null, billType: null })}
        />
      </CardContent>
    </Card>
  );
};

export default StaffPerformanceDetails;